/**
 * Canadian sales tax by province - client copy for the review screen,
 * settings and JobTread payloads.
 *
 * Mirrors server/src/utils/canadaTax.js (source: Retail Council of Canada
 * sales tax table, checked 2026-09-28). A server test compares the two
 * tables - change both together.
 *
 * GST and PST/QST are each charged on the pre-tax amount, side by side:
 * subtotal x GST + subtotal x PST. HST is one combined rate.
 */

export const GST_RATE = 5;

export const PROVINCES = {
  AB: { name: 'Alberta', regime: 'GST' },
  BC: { name: 'British Columbia', regime: 'GST_PST', pst: 7 },
  MB: { name: 'Manitoba', regime: 'GST_PST', pst: 7 },
  NB: { name: 'New Brunswick', regime: 'HST', hst: 15 },
  NL: { name: 'Newfoundland and Labrador', regime: 'HST', hst: 15 },
  NT: { name: 'Northwest Territories', regime: 'GST' },
  NS: { name: 'Nova Scotia', regime: 'HST', hst: 14 },
  NU: { name: 'Nunavut', regime: 'GST' },
  ON: { name: 'Ontario', regime: 'HST', hst: 13 },
  PE: { name: 'Prince Edward Island', regime: 'HST', hst: 15 },
  QC: { name: 'Quebec', regime: 'GST_QST', qst: 9.975 },
  SK: { name: 'Saskatchewan', regime: 'GST_PST', pst: 6 },
  YT: { name: 'Yukon', regime: 'GST' }
};


export function normalizeProvince(code) {
  const c = String(code || '').trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(PROVINCES, c) ? c : null;
}

/** "Canada – British Columbia" etc., sorted by name, for the country picker. */
export function provinceOptions() {
  return Object.entries(PROVINCES)
    .map(([code, p]) => ({ code, name: p.name, label: `Canada – ${p.name}`, value: `CA-${code}` }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Split a picker value ('CA-BC', 'NZ', ...) into { country, province }. */
export function splitCountryValue(value) {
  const v = String(value || '');
  if (v.startsWith('CA-')) return { country: 'CA', province: normalizeProvince(v.slice(3)) };
  return { country: v, province: null };
}

/** Picker value for a stored country + province. */
export function countryValue(country, province) {
  if (country === 'CA') {
    const p = normalizeProvince(province);
    return p ? `CA-${p}` : 'CA';
  }
  return country || '';
}

/** [{ code: 'GST'|'PST'|'QST'|'HST', rate, recoverable }] for a province. */
export function provinceTaxComponents(province) {
  const p = PROVINCES[normalizeProvince(province)];
  if (!p) return [];
  switch (p.regime) {
    case 'HST': return [{ code: 'HST', rate: p.hst, recoverable: true }];
    case 'GST_PST': return [
      { code: 'GST', rate: GST_RATE, recoverable: true },
      { code: 'PST', rate: p.pst, recoverable: false }
    ];
    case 'GST_QST': return [
      { code: 'GST', rate: GST_RATE, recoverable: true },
      { code: 'QST', rate: p.qst, recoverable: true }
    ];
    default: return [{ code: 'GST', rate: GST_RATE, recoverable: true }];
  }
}

/**
 * The combined sales-tax name as JobTread labels it: GST + PST is "PST+GST"
 * (JobTread's default, confirmed by Joe 2026-10-06). Used for the province's
 * sales tax as a whole (invoices). Bills split it: see provinceBillTaxName.
 */
export const JT_GST_PST_NAME = 'PST+GST';
export function jobTreadTaxName(codes) {
  const list = (codes || []).map(c => String(c).toUpperCase());
  if (list.length === 2 && list.includes('GST') && list.includes('PST')) return JT_GST_PST_NAME;
  return list.join(' + ');
}

export function provinceTaxName(province) {
  const parts = provinceTaxComponents(province).map(c => c.code);
  return parts.length ? jobTreadTaxName(parts) : 'GST/HST';
}

/**
 * Tax name on a JobTread BILL for the province: the recoverable part only
 * ("GST" in BC, "HST", "GST + QST"). PST is recorded separately on the bill as
 * non-recoverable tax. Invoices carry the combined tax (provinceTaxName).
 */
export function provinceBillTaxName(province) {
  const parts = provinceTaxComponents(province).filter(c => c.recoverable).map(c => c.code);
  return parts.length ? parts.join(' + ') : 'GST/HST';
}

export function provinceHasPst(province) {
  return provinceTaxComponents(province).some(c => !c.recoverable);
}

function round(n, dp = 2) {
  const f = Math.pow(10, dp);
  return Math.round(Number(((Number(n) || 0) * f).toFixed(6))) / f;
}

/**
 * Tax components on a pre-tax subtotal, each rounded to cents on its own:
 * [{ code, rate, recoverable, amount }].
 */
export function calculateCanadaTax(subtotal, province) {
  const base = Number(subtotal) || 0;
  const components = provinceTaxComponents(province).map(c => ({ ...c, amount: round(base * c.rate / 100) }));
  const totalTax = round(components.reduce((s, c) => s + c.amount, 0));
  return { components, totalTax, total: round(base + totalTax) };
}

/**
 * How a Canadian purchase (bill) is posted: always the full tax the supplier
 * charged, at the combined rate (GST + PST in BC = 12%). Both systems handle
 * PST themselves:
 *   - JobTread bills keep them apart: GST (recoverable) is the bill's tax,
 *     PST (non-recoverable) goes into the line costs and the bill's
 *     nonRecoverableTax (see recoverable* / nonRecoverable* below).
 *   - Xero purchase rates can carry a PST component ticked Non-recoverable,
 *     which Xero adds to the expense instead of claiming it.
 *
 * Takes the bill's own tax breakdown when present (what the supplier actually
 * charged, as reviewed), otherwise the province's rates.
 *
 * Returns:
 *   components     - [{ code, rate, amount, recoverable }]
 *   postedRatePct  - combined rate: match a Xero expense rate on this, and
 *                    send it to JobTread as the document rate
 *   postedTaxName  - label ("GST", "HST", "PST+GST", "GST + QST")
 *   postedTax      - total tax
 *   hasPst         - true when a non-recoverable PST is included, so the Xero
 *                    rate should have a Non-recoverable PST component
 */
export function planCanadaPurchaseTax({ province, subtotal, breakdown }) {
  const base = Number(subtotal) || 0;

  let components = Array.isArray(breakdown) && breakdown.length
    ? breakdown
        .filter(c => c && Number.isFinite(Number(c.rate)))
        .map(c => {
          const code = String(c.code || '').toUpperCase();
          return {
            code,
            rate: Number(c.rate),
            amount: Number.isFinite(Number(c.amount)) ? round(c.amount) : round(base * Number(c.rate) / 100),
            recoverable: code !== 'PST'
          };
        })
    : calculateCanadaTax(base, province).components;

  components = components.filter(c => c.rate > 0 || c.amount !== 0);

  return {
    components,
    postedRatePct: round(components.reduce((s, c) => s + c.rate, 0), 3),
    postedTaxName: components.length ? jobTreadTaxName(components.map(c => c.code)) : 'No Tax',
    postedTax: round(components.reduce((s, c) => s + c.amount, 0)),
    hasPst: components.some(c => !c.recoverable),
    // JobTread bills keep the two apart: recoverable (GST/HST/QST) is the bill's
    // tax, non-recoverable (PST) goes into the line costs + nonRecoverableTax.
    recoverableRatePct: round(components.filter(c => c.recoverable).reduce((s, c) => s + c.rate, 0), 3),
    recoverableTaxName: components.some(c => c.recoverable) ? components.filter(c => c.recoverable).map(c => c.code).join(' + ') : 'No Tax',
    nonRecoverableRatePct: round(components.filter(c => !c.recoverable).reduce((s, c) => s + c.rate, 0), 3),
    nonRecoverableTaxName: components.some(c => !c.recoverable) ? components.filter(c => !c.recoverable).map(c => c.code).join(' + ') : null
  };
}

/**
 * Split reviewed tax dollars across pre-tax line amounts. Each part is
 * proportional to the line, and the cent remainder goes on the largest line,
 * so the lines add back to the reviewed recoverable and non-recoverable totals.
 * Mirrors server/src/utils/canadaTax.js allocateBreakdownTax.
 * Returns [{ recoverable, nonRecoverable, tax }] aligned with lineAmounts.
 */
export function allocateBreakdownTax(lineAmounts, components) {
  const amounts = (Array.isArray(lineAmounts) ? lineAmounts : []).map(a => Number(a) || 0);
  const list = Array.isArray(components) ? components : [];
  const recoverableTotal = round(list.filter(c => c.recoverable).reduce((s, c) => s + (Number(c.amount) || 0), 0));
  const nonRecoverableTotal = round(list.filter(c => !c.recoverable).reduce((s, c) => s + (Number(c.amount) || 0), 0));
  if (!amounts.length) return [];

  const subtotal = amounts.reduce((s, a) => s + a, 0);
  const shares = amounts.map(amount => {
    if (!subtotal) return { recoverable: 0, nonRecoverable: 0 };
    return {
      recoverable: round(amount / subtotal * recoverableTotal),
      nonRecoverable: round(amount / subtotal * nonRecoverableTotal)
    };
  });

  let largest = 0;
  amounts.forEach((amount, i) => {
    if (Math.abs(amount) > Math.abs(amounts[largest])) largest = i;
  });
  const recSum = round(shares.reduce((s, x) => s + x.recoverable, 0));
  const nrSum = round(shares.reduce((s, x) => s + x.nonRecoverable, 0));
  shares[largest].recoverable = round(shares[largest].recoverable + recoverableTotal - recSum);
  shares[largest].nonRecoverable = round(shares[largest].nonRecoverable + nonRecoverableTotal - nrSum);

  return shares.map(s => ({
    recoverable: s.recoverable,
    nonRecoverable: s.nonRecoverable,
    tax: round(s.recoverable + s.nonRecoverable)
  }));
}

/**
 * What the matching Xero purchase tax rate should look like, for messages
 * and settings, e.g. "12% (GST 5% + PST 7%, PST ticked Non-recoverable)".
 */
export function xeroPurchaseRateHint(components) {
  const list = components || [];
  const total = round(list.reduce((s, c) => s + Number(c.rate || 0), 0), 3);
  if (list.length <= 1) return `${total}%`;
  const parts = list.map(c => `${c.code} ${c.rate}%`).join(' + ');
  const pst = list.some(c => c.recoverable === false || String(c.code).toUpperCase() === 'PST');
  return `${total}% (${parts}${pst ? ', PST ticked Non-recoverable' : ''})`;
}

/**
 * Split a Xero bill line's tax into PST (non-recoverable, a cost) and the
 * recoverable rest (GST), exactly as the server does when it posts to
 * JobTread (server/src/utils/vendorBillTax.js splitLineTax): PST on the line's
 * pre-tax amount rounded to cents, GST = the rest of the line's tax.
 * Returns { base, recoverable, nonRecoverable, jobCost } where jobCost is what
 * JobTread records as the line's cost (pre-tax + PST).
 */
export function splitXeroLineTax(line, lineAmountTypes, nonRecoverableRatePct) {
  const tax = Number(line?.taxAmount) || 0;
  const amount = Number(line?.lineAmount) || 0;
  const base = lineAmountTypes === 'Inclusive' ? amount - tax : amount;
  const pct = Number(nonRecoverableRatePct) || 0;
  let nonRecoverable = 0;
  if (tax && pct) {
    nonRecoverable = Math.sign(tax) * round(Math.abs(base) * pct / 100);
    if (Math.abs(nonRecoverable) > Math.abs(tax)) nonRecoverable = tax;
  }
  nonRecoverable = round(nonRecoverable);
  return { base, recoverable: round(tax - nonRecoverable), nonRecoverable, jobCost: round(base + nonRecoverable) };
}

/**
 * Tax config for a Canadian bill that is already in Xero: the bill's own rate
 * rather than a fixed 5%. When it matches the province, the config is the
 * recoverable part (GST in BC) because JobTread records PST separately.
 * The rate is taken over the TAXED lines only, so tax-exempt lines on the same
 * bill don't dilute it (3 exempt lines + 1 HST line is still 13% HST, not 3.25%).
 * Returns null when the bill has no usable totals.
 */
export function canadaTaxConfigForXeroBill(bill, province) {
  const inclusive = bill?.lineAmountTypes === 'Inclusive';
  let sub = 0;
  let tax = 0;
  for (const li of (Array.isArray(bill?.lineItems) ? bill.lineItems : [])) {
    const t = Number(li?.taxAmount) || 0;
    if (!t) continue;
    const amount = Number(li?.lineAmount) || 0;
    sub += inclusive ? amount - t : amount;
    tax += t;
  }
  if (!sub) {
    sub = Number(bill?.subTotal);
    tax = Number(bill?.totalTax);
  }
  if (!Number.isFinite(sub) || !Number.isFinite(tax) || sub <= 0) return null;
  const ratePct = round((tax / sub) * 100, 3);
  if (ratePct <= 0) return { rate: 0, name: 'No Tax' };
  const near = (a, b) => Math.abs(a - b) <= 0.1;
  const comps = provinceTaxComponents(province);
  const total = comps.reduce((s, c) => s + c.rate, 0);
  const recoverable = comps.filter(c => c.recoverable).reduce((s, c) => s + c.rate, 0);
  // The JobTread bill's tax is the recoverable part only; PST (non-recoverable)
  // is split out server-side from the line's Xero tax rate.
  if (comps.length && (near(ratePct, total) || near(ratePct, recoverable))) {
    const includesPst = near(ratePct, total) && comps.some(c => !c.recoverable);
    return { rate: round(recoverable / 100, 6), name: provinceBillTaxName(province), nonRecoverableName: includesPst ? 'PST' : null };
  }
  if (near(ratePct, GST_RATE)) return { rate: 0.05, name: 'GST' };
  return { rate: round(ratePct / 100, 6), name: 'GST/HST' };
}
